import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../config';
import './SearchPage.css';

const SearchPage = () => {
  const { token, tenant } = useAuth();
  const [query, setQuery] = useState('');
  const [piiType, setPiiType] = useState('');
  const [piiTypes, setPiiTypes] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [detokenized, setDetokenized] = useState({});
  const [rootTokens, setRootTokens] = useState({});
  const [groupedTokens, setGroupedTokens] = useState({});
  const [selected, setSelected] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [tenantUid, setTenantUid] = useState(tenant?.uid || '');
  const [error, setError] = useState('');
  const [initialSearchTriggered, setInitialSearchTriggered] = useState(false);
  const [activeRootToken, setActiveRootToken] = useState(null);
  const [tokenizedObject, setTokenizedObject] = useState(null);
  const [isJsonDetokenized, setIsJsonDetokenized] = useState(false);
  const [availableVersions, setAvailableVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [expandedRoots, setExpandedRoots] = useState(new Set());
  const [lineageModalToken, setLineageModalToken] = useState(null);
  const [lineageData, setLineageData] = useState(null);
  const [lineageLoading, setLineageLoading] = useState(false);
  const [lineageError, setLineageError] = useState('');

  const getHeaders = (extraHeaders = {}) => {
    const headers = { ...extraHeaders };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (apiKey) headers['X-API-KEY'] = apiKey;
    if (tenantUid) headers['X-TENANT-ID'] = tenantUid;
    return headers;
  };

  useEffect(() => {
    if (tenant?.uid) {
      setTenantUid(tenant.uid);
    } else {
      const savedTenantUid = localStorage.getItem('vaultize_tenant_uid');
      if (savedTenantUid) setTenantUid(savedTenantUid);
    }

    const savedApiKey = localStorage.getItem('vaultize_api_key');
    if (savedApiKey) {
      setApiKey(savedApiKey);
    } else if (token) {
      fetch(`${API_BASE}/dashboard/api-keys`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => res.ok ? res.json() : [])
      .then(keys => {
        if (Array.isArray(keys) && keys.length > 0) {
          const activeKey = keys.find(k => (k.enabled && !k.revoked)) || keys[0];
          const val = activeKey.apiKeyPreview || activeKey.apiKey || activeKey.maskedKey;
          if (val) setApiKey(val);
        }
      })
      .catch(() => {});
    }
  }, [tenant, token]);

  useEffect(() => {
    if ((apiKey && tenantUid) || token) fetchPiiTypes();
  }, [apiKey, tenantUid, token]);

    // Reset pagination when filters change
    useEffect(() => {
      setPage(0);
    }, [query, piiType]);

  useEffect(() => {
    if (initialSearchTriggered) handleSearch();
  }, [page, size]);

  const fetchPiiTypes = async () => {
    try {
      const res = await fetch(`${API_BASE}/search/pii-types`, {
        headers: getHeaders(),
      });
      if (!res.ok) return;
      const types = await res.json();
      setPiiTypes(Array.from(types).sort());
    } catch (e) {
      console.error(e);
    }
  };

  const handleSearch = async () => {
    if (!token && (!apiKey || !tenantUid)) return;
    setLoading(true);
    setError('');
    setTokens([]);
    setSelected([]);
    setDetokenized({});
    setRootTokens({});
    setGroupedTokens({});
    setExpandedRoots(new Set());

    try {
      const res = await fetch(`${API_BASE}/search`, {
        method: 'POST',
        headers: getHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({ query, piiType, page, size }),
      });
      if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
      const data = await res.json();
      setTokens(data.tokens || []);
      setTotal(data.totalMatches || 0);
      setInitialSearchTriggered(true);

      const rt = {};
      const grouped = {};
      for (const { token: tVal, rootToken } of (data.tokens || [])) {
        rt[tVal] = rootToken;
        if (!grouped[rootToken]) grouped[rootToken] = [];
        grouped[rootToken].push(tVal);
      }
      setRootTokens(rt);
      setGroupedTokens(grouped);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchTokenizedObjectByRootToken = async (rootToken) => {
    setActiveRootToken(rootToken);
    setIsJsonDetokenized(false);
    try {
      const versionRes = await fetch(`${API_BASE}/search/tokenized-objects/versions/${rootToken}`, {
        headers: getHeaders()
      });
      const versions = await versionRes.json();
      setAvailableVersions(versions);
      const latest = versions[0];
      setSelectedVersion(latest);

      const dataRes = await fetch(`${API_BASE}/search/tokenized-objects/${rootToken}?version=${latest}`, {
        headers: getHeaders()
      });
      const json = await dataRes.json();
      setTokenizedObject(json);
    } catch (e) {
      setTokenizedObject({ error: 'Could not fetch tokenized object or version list' });
    }
  };

  const handleViewLineage = async (tok) => {
    setLineageModalToken(tok);
    setLineageLoading(true);
    setLineageError('');
    setLineageData(null);
    try {
      const res = await fetch(`${API_BASE}/search/lineage/${encodeURIComponent(tok)}`, {
        headers: getHeaders(),
      });
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.message || `Failed to fetch lineage: ${res.status}`);
      }
      const data = await res.json();
      setLineageData(data);
    } catch (err) {
      setLineageError(err.message);
    } finally {
      setLineageLoading(false);
    }
  };

  const handleDetokenize = async () => {
    try {
      const res = await fetch(`${API_BASE}/detokenize/bulk`, {
        method: 'POST',
        headers: getHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({ tokens: selected })
      });
      const data = await res.json();
      // ✅ normalize for UI
      const flat = {};
      Object.entries(data.results || {}).forEach(([token, result]) => {
        if (result.status === 'SUCCESS') {
          flat[token] = result.value;
        }
      });

    setDetokenized(flat);
    } catch {
      setError('❌ Detokenization error');
    }
  };

  const handleDownload = () => {
    const rows = selected.map(t => ({ token: t, value: detokenized[t] || '' }));
    const csv = ['Token,PII Value'].concat(rows.map(r => `${r.token},"${r.value}"`)).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'detokenized.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totalPages = Math.ceil(total / size);

  return (
    <div className="container playground">
      <h2>🔍 Secure PII Token Search</h2>

      <div className="input-headers">
        <label className="form-field">
          API Key:
          <input
            className="input"
            value={apiKey}
            onChange={e => setApiKey(e.target.value)}
            placeholder="Enter API Key"
          />
        </label>
        <label className="form-field">
          Tenant UID:
          <input
            className="input"
            value={tenantUid}
            onChange={e => setTenantUid(e.target.value)}
            placeholder="Enter Tenant UID"
          />
        </label>
      </div>

      <div className="search-bar form-row">
        <input
          className="input"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search..."
        />
        <select
          className="input"
          value={piiType}
          onChange={e => setPiiType(e.target.value)}
        >
          <option value="ALL">All Types</option>
          {piiTypes.map(t => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select
          className="input"
          value={size}
          onChange={e => setSize(parseInt(e.target.value))}
        >
          {[10, 20, 50].map(n => (
            <option key={n} value={n}>
              {n} per page
            </option>
          ))}
        </select>
        <button
          className="btn btn-secondary"
          type="button"
          onClick={fetchPiiTypes}
          aria-label="Refresh PII Types"
        >
          🔄 Refresh
        </button>
        <button
          className="btn btn-primary"
          onClick={() => {
            setPage(0);
            handleSearch();
          }}
          disabled={loading}
          aria-busy={loading}
          aria-label="Search Tokens"
        >
          {loading ? 'Searching...' : 'Search'}
        </button>
      </div>

      <div className="response-section">
        {error && <pre className="error">{error}</pre>}

        {Object.keys(groupedTokens).length > 0 && (
          <>
            <p>
              Grouped by Root Token ({Object.keys(groupedTokens).length} groups)
            </p>

            <button className="btn btn-primary" onClick={handleDetokenize}>
              🔓 Detokenize Selected
            </button>
            <button
              className="btn btn-primary"
              onClick={handleDownload}
              disabled={!Object.keys(detokenized).length}
            >
              ⬇️ Download CSV
            </button>

            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => {
                const allRoots = Object.keys(groupedTokens);
                const isAllExpanded = allRoots.every(r => expandedRoots.has(r));
                setExpandedRoots(new Set(isAllExpanded ? [] : allRoots));
              }}
            >
              {Object.keys(groupedTokens).every(r => expandedRoots.has(r))
                ? '➖ Collapse All'
                : '➕ Expand All'}
            </button>

            <label className="form-field" style={{ userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={Object.values(groupedTokens)
                  .flat()
                  .every((t) => selected.includes(t))}
                onChange={(e) => {
                  const allTokens = Object.values(groupedTokens).flat();
                  const allRoots = Object.keys(groupedTokens);
                  const checked = e.target.checked;
                  setSelected(checked ? allTokens : []);
                  setExpandedRoots(new Set(checked ? allRoots : []));
                }}
              />{' '}
              Select All ({Object.values(groupedTokens).flat().length} tokens)
            </label>

            {Object.entries(groupedTokens).map(([rootToken, tokenList]) => (
              <details
                key={rootToken}
                className="token-group"
                open={expandedRoots.has(rootToken)}
                onToggle={(e) => {
                  setExpandedRoots((prev) => {
                    const next = new Set(prev);
                    if (e.target.open) next.add(rootToken);
                    else next.delete(rootToken);
                    return next;
                  });
                }}
              >
                <summary>
                  📦 Root Token: <code>{rootToken}</code> ({tokenList.length}{' '}
                  tokens)
                </summary>
                <table>
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          checked={tokenList.every((token) =>
                            selected.includes(token)
                          )}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setSelected((prev) => {
                              return checked
                                ? [...new Set([...prev, ...tokenList])]
                                : prev.filter((t) => !tokenList.includes(t));
                            });
                          }}
                        />
                      </th>
                      <th>Token</th>
                      <th>PII Value</th>
                      <th>View Object</th>
                      <th>Lineage Trace</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tokenList.map((token) => (
                      <tr key={token}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selected.includes(token)}
                            onChange={() => {
                              setSelected((prev) =>
                                prev.includes(token)
                                  ? prev.filter((t) => t !== token)
                                  : [...prev, token]
                              );
                            }}
                          />
                        </td>
                        <td>
                          <code>{token}</code>
                        </td>
                        <td>{detokenized[token] || ''}</td>
                        <td>
                          <button
                            className="btn btn-ghost"
                            type="button"
                            onClick={() =>
                              fetchTokenizedObjectByRootToken(rootToken)
                            }
                          >
                            📄 View
                          </button>
                        </td>
                        <td>
                          <button
                            className="btn btn-ghost btn-lineage-action"
                            type="button"
                            onClick={() => handleViewLineage(token)}
                            title="Inspect cryptographic lifecycle lineage graph"
                          >
                            🧭 Trace
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            ))}
          </>
        )}

        {tokenizedObject && (
          <div className="tokenized-object-view page-card">
            <h4>🧾 Tokenized Object</h4>
            {tokenizedObject.createdAt && (
              <p>
                <strong>Created:</strong>{' '}
                {new Date(tokenizedObject.createdAt).toLocaleString()}
              </p>
            )}

            {availableVersions.length > 0 && (
              <label>
                Version:
                <select
                  value={selectedVersion}
                  onChange={async (e) => {
                    const version = parseInt(e.target.value);
                    setSelectedVersion(version);
                    try {
                      const res = await fetch(
                        `${API_BASE}/search/tokenized-objects/${tokenizedObject.rootToken}?version=${version}`,
                        {
                          headers: getHeaders(),
                        }
                      );
                      const json = await res.json();
                      setTokenizedObject(json);
                      setIsJsonDetokenized(false);
                    } catch {
                      alert('❌ Failed to load version');
                    }
                  }}
                  className="input"
                >
                  {availableVersions.map((v) => (
                    <option key={v} value={v}>
                      v{v}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <pre>{JSON.stringify(tokenizedObject.tokenizedJson || tokenizedObject, null, 2)}</pre>

            {isJsonDetokenized && (
              <>
                <textarea
                  rows={10}
                  className="input"
                  value={JSON.stringify(tokenizedObject.tokenizedJson, null, 2)}
                  onChange={(e) => {
                    try {
                      const json = JSON.parse(e.target.value);
                      setTokenizedObject((prev) => ({ ...prev, tokenizedJson: json }));
                    } catch {}
                  }}
                />
                <button
                  className="btn btn-primary"
                  onClick={async () => {
                    try {
                      const res = await fetch(`${API_BASE}/tokenize/json`, {
                        method: 'PUT',
                        headers: getHeaders({
                          'Content-Type': 'application/json',
                        }),
                        body: JSON.stringify({
                          rootToken: tokenizedObject.rootToken,
                          data: tokenizedObject.tokenizedJson,
                        }),
                      });
                      await res.json();
                      await fetchTokenizedObjectByRootToken(tokenizedObject.rootToken);
                    } catch {
                      alert('❌ Failed to save new version');
                    }
                  }}
                >
                  💾 Save as New Version
                </button>
              </>
            )}

            <button
              className="btn btn-primary"
              onClick={async () => {
                try {
                  const res = await fetch(`${API_BASE}/detokenize/json`, {
                    method: 'POST',
                    headers: getHeaders({
                      'Content-Type': 'application/json',
                    }),
                    body: JSON.stringify({ tokenizedData: tokenizedObject.tokenizedJson }),
                  });
                  const json = await res.json();
                  setTokenizedObject((prev) => ({ ...prev, tokenizedJson: json.data }));
                  setIsJsonDetokenized(true);
                } catch (e) {
                  alert('❌ Failed to detokenize full JSON');
                }
              }}
            >
              🔓 Detokenize Full JSON
            </button>
          </div>
        )}

        <div className="pagination form-row">
          <button className="btn btn-secondary" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            Previous
          </button>
          <span>
            Page {page + 1} of {totalPages}
          </span>
          <button
            className="btn btn-secondary"
            disabled={page + 1 >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>

        {/* Token Lifecycle Lineage Modal */}
        {lineageModalToken && (
          <div className="modal-overlay lineage-modal-overlay" onClick={() => setLineageModalToken(null)}>
            <div className="modal-content lineage-modal" onClick={(e) => e.stopPropagation()}>
              <div className="lineage-modal-header">
                <div>
                  <h3 className="lineage-title">🧭 Token Lifecycle Lineage Trace</h3>
                  <p className="lineage-sub">
                    Zero-knowledge cryptographic custody audit & event sequence
                  </p>
                </div>
                <button
                  className="policy-modal-close"
                  onClick={() => setLineageModalToken(null)}
                  aria-label="Close lineage modal"
                >
                  ✕
                </button>
              </div>

              {lineageLoading && (
                <div className="lineage-loading-state">
                  <div className="lineage-spinner"></div>
                  <p>Decrypting audit trail & assembling cryptographic lifecycle graph...</p>
                </div>
              )}

              {lineageError && (
                <div className="policy-error" style={{ margin: "1rem 0" }}>
                  {lineageError}
                </div>
              )}

              {lineageData && (
                <div className="lineage-modal-body">
                  <div className="lineage-meta-cards">
                    <div className="lineage-meta-card">
                      <span className="meta-label">Vault Token</span>
                      <code className="meta-val">{lineageData.token}</code>
                    </div>
                    <div className="lineage-meta-card">
                      <span className="meta-label">Root Token Enclave</span>
                      <code className="meta-val">{lineageData.rootToken || "Single Token"}</code>
                    </div>
                    <div className="lineage-meta-card">
                      <span className="meta-label">PII Classification</span>
                      <span className="meta-val pii-badge">{lineageData.piiType}</span>
                    </div>
                    <div className="lineage-meta-card">
                      <span className="meta-label">Total Audit Events</span>
                      <span className="meta-val count-badge">{lineageData.totalEvents} recorded</span>
                    </div>
                  </div>

                  <div className="lineage-timeline">
                    {lineageData.events && lineageData.events.map((evt, idx) => {
                      const isSuccess = evt.status === "SUCCESS";
                      const stageIcon = evt.stage === "MINTED" ? "🌱" : evt.stage === "INDEXED" ? "🔍" : "🔓";

                      return (
                        <div key={idx} className={`timeline-node stage-${evt.stage ? evt.stage.toLowerCase() : "event"}`}>
                          <div className="timeline-marker">
                            <span className="marker-icon">{stageIcon}</span>
                            {idx < lineageData.events.length - 1 && <div className="timeline-connector" />}
                          </div>
                          <div className="timeline-card">
                            <div className="timeline-card-header">
                              <div className="timeline-title-group">
                                <span className={`timeline-stage-badge stage-${evt.stage ? evt.stage.toLowerCase() : "default"}`}>
                                  {evt.stage}
                                </span>
                                <span className="timeline-timestamp">
                                  {new Date(evt.timestamp).toLocaleString()}
                                </span>
                              </div>
                              <span className={`status-badge ${isSuccess ? "active" : "suspended"}`}>
                                {evt.status}
                              </span>
                            </div>

                            <p className="timeline-details">{evt.details}</p>

                            <div className="timeline-meta-grid">
                              <div>
                                <span className="meta-sub">Actor:</span> <strong>{evt.actor || "System"}</strong>
                              </div>
                              <div>
                                <span className="meta-sub">Role:</span> <code>{evt.role || "SYSTEM"}</code>
                              </div>
                              <div>
                                <span className="meta-sub">Source:</span> <span>{evt.source || "API"}</span>
                              </div>
                              <div>
                                <span className="meta-sub">Origin IP:</span> <code>{evt.ipAddress || "127.0.0.1"}</code>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="modal-actions" style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => setLineageModalToken(null)}
                >
                  Close Trace
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;
